import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/* ═══════════════════════════════════════════════════
   Kalendar Kempen SESAAT — dipapar di Dashboard

   TIGA LAPISAN bertindan:
     1. AUTO — kempen platform (Shopee/TikTok Shop). Semuanya FORMULA tetap,
        jadi betul untuk 2026, 2027, 2030 tanpa sesiapa kemas kini.
          · Tarikh berkembar  → hari ke-N bulan ke-N (1.1 … 12.12)
          · Mid-month sale    → 15hb setiap bulan
          · Payday sale       → 25hb setiap bulan
     2. AUTO — cuti umum + cuti sekolah + tarikh komersial (Hari Ibu, Black
        Friday …). Cuti umum ditaip per tahun (lihat HOLIDAYS); tarikh komersial
        dikira sendiri (cth Hari Ibu = Ahad ke-2 bulan Mei).
     3. ADMIN — nota, event tambahan, dan event auto yang disorok. Duduk dalam
        Firestore `config/campaign_calendar`, admin edit terus di Dashboard.

   ⚠️ SENGAJA TIADA panggilan API luar. Tarikh cuti setahun sekali sahaja
   berubah, dan API cuti Malaysia yang percuma semuanya projek hobi — kalau
   server mereka mati, kalendar semua pelanggan jadi kosong. Isi tahun baharu
   secara manual dalam HOLIDAYS di bawah (ambil dari kabinet.gov.my).

   ── GAYA AYAT ──
   Formal Melayu tetapi mudah faham. Terma teknikal kekal English (sale, payday,
   campaign, live, content, niche).
   ═══════════════════════════════════════════════════ */

export const CALENDAR_DOC = 'campaign_calendar';      // config/campaign_calendar
const SESSION_CACHE_KEY = 'sesaat_campaign_calendar_cache';

export const MONTH_MY = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
    'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];
export const WEEKDAY_MY = ['Ahd', 'Isn', 'Sel', 'Rab', 'Kha', 'Jum', 'Sab'];

// Tahun yang cuti umumnya sudah ditaip penuh. Luar julat ni, lapisan kempen
// platform + tarikh komersial tetap jalan (formula) — cuma cuti sahaja tiada.
export const HOLIDAY_YEARS = [2026];

// ─── Helper tarikh (semua ikut waktu KL, UTC+8) ───────
const KL_OFFSET_MS = 8 * 60 * 60 * 1000;
const pad = (n) => String(n).padStart(2, '0');

export const dayKey = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;

export const klToday = () => {
    const my = new Date(Date.now() + KL_OFFSET_MS);
    return dayKey(my.getUTCFullYear(), my.getUTCMonth() + 1, my.getUTCDate());
};

export const daysInMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
export const firstWeekday = (y, m) => new Date(Date.UTC(y, m - 1, 1)).getUTCDay();

// Beza hari antara dua day-key (b − a). Guna UTC supaya tiada drift jam.
export function daysBetween(aKey, bKey) {
    const [ay, am, ad] = aKey.split('-').map(Number);
    const [by, bm, bd] = bKey.split('-').map(Number);
    return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
}

export function shiftKey(key, deltaDays) {
    const [y, m, d] = key.split('-').map(Number);
    const t = new Date(Date.UTC(y, m - 1, d) + deltaDays * 86400000);
    return dayKey(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

// Hari-ke-N dalam bulan, cth Ahad ke-2 Mei → nthWeekday(2026, 5, 0, 2)
function nthWeekday(y, m, weekday, n) {
    const firstDow = firstWeekday(y, m);
    const day = 1 + ((weekday - firstDow + 7) % 7) + (n - 1) * 7;
    return day <= daysInMonth(y, m) ? dayKey(y, m, day) : null;
}

export function formatDateMY(key) {
    if (!key) return '';
    const [y, m, d] = key.split('-').map(Number);
    return `${d} ${MONTH_MY[m - 1]} ${y}`;
}

export function weekdayMY(key) {
    const [y, m, d] = key.split('-').map(Number);
    return WEEKDAY_MY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}

/* ─── LAPISAN 1: kempen platform (formula tulen) ───────
   Shopee Malaysia sahkan sendiri ada 3 jenis sale setiap bulan: tarikh
   berkembar, mid-month, dan payday 25hb. TikTok Shop ikut corak sama. */

// Keutamaan tarikh berkembar — 11.11 paling besar, diikuti 12.12/9.9/10.10.
const DOUBLE_TIER = { 1: 'small', 2: 'small', 3: 'small', 4: 'small', 5: 'small', 6: 'mid', 7: 'mid', 8: 'mid', 9: 'big', 10: 'big', 11: 'big', 12: 'big' };

function platformEvents(y, m) {
    const out = [];
    const dd = m;                                    // 1.1, 2.2 … 12.12
    if (dd <= daysInMonth(y, m)) {
        out.push({
            key: `p:double:${dayKey(y, m, dd)}`,
            date: dayKey(y, m, dd),
            title: `${m}.${m} Sale`,
            kind: 'platform',
            tier: DOUBLE_TIER[m],
            hint: m === 11 ? 'Sale terbesar tahun ini di Shopee dan TikTok Shop.'
                : DOUBLE_TIER[m] === 'big' ? 'Trafik pembeli tinggi — sedia stok konten awal.'
                    : 'Sale tarikh berkembar bulanan.',
        });
    }
    out.push({
        key: `p:mid:${dayKey(y, m, 15)}`,
        date: dayKey(y, m, 15),
        title: 'Mid-Month Sale',
        kind: 'platform',
        tier: 'small',
        hint: 'Sale pertengahan bulan.',
    });
    out.push({
        key: `p:payday:${dayKey(y, m, 25)}`,
        date: dayKey(y, m, 25),
        title: 'Payday Sale',
        kind: 'platform',
        tier: 'mid',
        hint: 'Hari gaji — kuasa beli paling tinggi dalam bulan.',
    });
    return out;
}

/* ─── LAPISAN 2a: tarikh komersial (dikira, bukan ditaip) ─── */
function commerceEvents(y, m) {
    const out = [];
    const add = (date, title, tier, hint, lead) => { if (date) out.push({ key: `c:${title}:${date}`, date, title, kind: 'commerce', tier, hint, lead }); };

    if (m === 2) add(dayKey(y, 2, 14), 'Valentine\'s Day', 'mid', 'Hadiah, barang couple, kecantikan.', 14);
    if (m === 5) add(nthWeekday(y, 5, 0, 2), 'Hari Ibu', 'mid', 'Hadiah, kecantikan, barang dapur.', 14);
    if (m === 6) add(nthWeekday(y, 6, 0, 3), 'Hari Bapa', 'mid', 'Hadiah, gadget, barang lelaki.', 14);
    if (m === 11) {
        // Black Friday = sehari selepas Khamis ke-4 November (ikut Thanksgiving AS)
        const thanksgiving = nthWeekday(y, 11, 4, 4);
        if (thanksgiving) {
            add(shiftKey(thanksgiving, 1), 'Black Friday', 'mid', 'Elektronik, gadget, barang jenama.', 14);
            add(shiftKey(thanksgiving, 4), 'Cyber Monday', 'small', 'Sambungan Black Friday — fokus online.', 7);
        }
    }
    return out;
}

/* ─── LAPISAN 2b: cuti umum + cuti sekolah ────────────
   Sumber 2026: gazet Jabatan Perdana Menteri (kabinet.gov.my) dan takwim
   akademik KPM. `scope` hanya untuk paparan — tiada penapisan per negeri,
   sebab pembeli TikTok datang dari seluruh Malaysia.

   `until` = event berbilang hari (cuti sekolah).
   `lead`  = berapa hari sebelum tarikh, konten patut mula keluar. */

const HOLIDAYS = {
    2026: [
        { date: '2026-01-01', title: 'Tahun Baharu', tier: 'small', lead: 7 },
        { date: '2026-02-01', title: 'Thaipusam', tier: 'small', scope: 'Sebahagian negeri', lead: 7 },
        { date: '2026-02-17', until: '2026-02-18', title: 'Tahun Baharu Cina', tier: 'big', lead: 30, hint: 'Baju, hamper, hiasan rumah, barang hadiah. Belanja bermula awal — mula konten pertengahan Januari.' },
        { date: '2026-02-19', title: 'Awal Ramadan', tier: 'mid', lead: 14, hint: 'Ramadan 19 Feb – 20 Mac. Barang dapur, jubah, kurma, persiapan Raya.' },
        { date: '2026-03-07', title: 'Nuzul Al-Quran', tier: 'small', scope: 'Sebahagian negeri', lead: 7 },
        { date: '2026-03-21', until: '2026-03-22', title: 'Hari Raya Aidilfitri', tier: 'big', lead: 21, hint: 'Puncak belanja terbesar tahun ini. Baju Raya, kuih, hiasan, hadiah. Puncak sebenar 1–18 Mac.' },
        { date: '2026-04-03', title: 'Good Friday', tier: 'small', scope: 'Sabah & Sarawak', lead: 7 },
        { date: '2026-05-01', title: 'Hari Pekerja', tier: 'small', lead: 7 },
        { date: '2026-05-27', until: '2026-05-28', title: 'Hari Raya Aidiladha', tier: 'mid', lead: 14, hint: 'Barang dapur, jubah, peralatan korban.' },
        { date: '2026-05-30', until: '2026-05-31', title: 'Pesta Kaamatan', tier: 'mid', scope: 'Sabah & Labuan', lead: 14 },
        { date: '2026-05-31', title: 'Hari Wesak', tier: 'small', lead: 7 },
        { date: '2026-06-01', title: 'Hari Keputeraan Agong', tier: 'small', lead: 7 },
        { date: '2026-06-01', until: '2026-06-02', title: 'Hari Gawai Dayak', tier: 'mid', scope: 'Sarawak', lead: 14 },
        { date: '2026-06-17', title: 'Awal Muharam', tier: 'small', lead: 7 },
        { date: '2026-08-25', title: 'Maulidur Rasul', tier: 'small', lead: 7 },
        { date: '2026-08-31', title: 'Hari Merdeka', tier: 'big', lead: 14, hint: 'Jatuh hari Isnin — long weekend 29–31 Ogos. Baju tema, barang patriotik, hiasan. Mula konten 17 Ogos.' },
        { date: '2026-09-16', title: 'Hari Malaysia', tier: 'mid', lead: 14, hint: 'Sambungan momentum Merdeka — tema patriotik masih laku.' },
        { date: '2026-11-08', title: 'Deepavali', tier: 'big', lead: 21, hint: 'Baju, barang hiasan, kuih, hadiah. Mula konten sekitar 20 Oktober.' },
        { date: '2026-12-25', title: 'Hari Krismas', tier: 'big', lead: 21, hint: 'Hadiah, hiasan, elektronik. Bertindan dengan 12.12 — kempen boleh disatukan.' },
    ],
};

const SCHOOL_HOLIDAYS = {
    2026: [
        { date: '2026-08-28', until: '2026-09-06', title: 'Cuti Penggal 2 (sekolah)', tier: 'small', lead: 10, hint: 'Kumpulan A 28 Ogos – 5 Sep, Kumpulan B 29 Ogos – 6 Sep. Barang travel, mainan, aktiviti keluarga.' },
        { date: '2026-12-05', until: '2026-12-31', title: 'Cuti akhir tahun (sekolah)', tier: 'mid', lead: 14, hint: 'Cuti panjang 27 hari. Barang travel, mainan, hadiah — bertindan dengan 12.12 dan Krismas.' },
    ],
};

const DEFAULT_LEAD = { big: 21, mid: 14, small: 7 };

function holidayEvents(y) {
    const src = [
        ...(HOLIDAYS[y] || []).map(h => ({ ...h, kind: 'holiday' })),
        ...(SCHOOL_HOLIDAYS[y] || []).map(h => ({ ...h, kind: 'school' })),
    ];
    return src.map(h => ({ ...h, key: `h:${h.kind}:${h.date}:${h.title}` }));
}

/* ─── Gabung semua lapisan auto untuk satu bulan ───────
   Event berbilang hari (cuti sekolah) dipulangkan sekali sahaja, pada tarikh
   mula — pengembangan ke setiap hari dibuat dalam buildDayMap.
   ⚠️ `lead` SENGAJA tidak diisi di sini bila event tiada nilai sendiri — ia
   bergantung pada tier, dan tier boleh diubah admin. monthEvents yang isi. */
export function autoEventsForMonth(y, m) {
    const monthPrefix = `${y}-${pad(m)}`;
    const spansMonth = (e) => {
        if (e.date.startsWith(monthPrefix)) return true;
        return !!(e.until && e.date < monthPrefix && e.until >= monthPrefix);   // bermula bulan lepas, masih berjalan
    };
    return [
        ...platformEvents(y, m),
        ...commerceEvents(y, m),
        ...holidayEvents(y).filter(spansMonth),
    ];
}

// ─── Bentuk data admin (Firestore) ────────────────────
export function normalizeCalendar(raw) {
    const src = raw && typeof raw === 'object' ? raw : {};
    return {
        intro: typeof src.intro === 'string' ? src.intro
            : 'Tarikh kempen dan cuti penting sepanjang tahun. Sedia konten awal supaya anda tidak tercicir masa trafik pembeli melonjak.',
        notes: (src.notes && typeof src.notes === 'object' && !Array.isArray(src.notes)) ? { ...src.notes } : {},
        // Admin boleh naik/turunkan keutamaan mana-mana event auto tanpa sentuh kod.
        tiers: (src.tiers && typeof src.tiers === 'object' && !Array.isArray(src.tiers))
            ? Object.fromEntries(Object.entries(src.tiers).filter(([, v]) => ['big', 'mid', 'small'].includes(v)))
            : {},
        hidden: Array.isArray(src.hidden) ? src.hidden.filter(k => typeof k === 'string') : [],
        events: (Array.isArray(src.events) ? src.events : [])
            .filter(e => e && typeof e.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(e.date))
            .map((e, i) => ({
                id: e.id || `e_${i}`,
                date: e.date,
                until: (typeof e.until === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(e.until) && e.until > e.date) ? e.until : null,
                title: e.title || '(tanpa tajuk)',
                tier: ['big', 'mid', 'small'].includes(e.tier) ? e.tier : 'mid',
                hint: typeof e.hint === 'string' ? e.hint : '',
                lead: Number.isFinite(e.lead) ? Math.max(0, Math.min(90, e.lead)) : null,
                kind: 'custom',
            })),
    };
}

// ─── Bacaan ───────────────────────────────────────────
// Dashboard guna versi cache (sekali per sesi browser) supaya tukar page tidak
// mencetuskan bacaan berulang. Mod edit admin baca segar selepas simpan.
export async function loadCampaignCalendar() {
    const snap = await getDoc(doc(db, 'config', CALENDAR_DOC));
    const data = normalizeCalendar(snap.exists() ? snap.data() : null);
    try { sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(data)); } catch { /* abaikan */ }
    return { data, exists: snap.exists() };
}

export async function loadCampaignCalendarCached() {
    try {
        const cached = sessionStorage.getItem(SESSION_CACHE_KEY);
        if (cached) return normalizeCalendar(JSON.parse(cached));
    } catch { /* cache rosak — baca segar */ }
    try {
        const { data } = await loadCampaignCalendar();
        return data;
    } catch {
        return normalizeCalendar(null);      // fail-safe: lapisan auto tetap jalan
    }
}

export function clearCalendarCache() {
    try { sessionStorage.removeItem(SESSION_CACHE_KEY); } catch { /* abaikan */ }
}

export const TIER_RANK = { big: 0, mid: 1, small: 2 };

/* ─── Senarai event satu bulan (auto + admin, tolak yang disorok) ───
   `showHidden` = true untuk admin dalam mod edit, supaya event yang disorok
   masih boleh dilihat dan dibuka semula. */
export function monthEvents(y, m, config, showHidden = false) {
    const cfg = config || normalizeCalendar(null);
    const monthPrefix = `${y}-${pad(m)}`;
    const custom = cfg.events
        .filter(e => e.date.startsWith(monthPrefix) || (e.until && e.date < monthPrefix && e.until >= monthPrefix))
        .map(e => ({ ...e, key: `x:${e.id}` }));

    return [...autoEventsForMonth(y, m), ...custom]
        .map(e => {
            // Tier admin (kalau ada) mengatasi tier asal — dan lead ikut tier akhir,
            // supaya event yang dinaikkan ke "Besar" dapat masa sedia konten lebih awal.
            const tier = cfg.tiers[e.key] || e.tier;
            return {
                ...e,
                tier,
                lead: e.lead ?? DEFAULT_LEAD[tier] ?? 7,
                note: cfg.notes[e.key] || '',
                hidden: cfg.hidden.includes(e.key),
            };
        })
        .filter(e => showHidden || !e.hidden)
        .sort((a, b) => a.date.localeCompare(b.date) || TIER_RANK[a.tier] - TIER_RANK[b.tier]);
}

/* ─── Peta hari → event (untuk grid kalendar) ──────────
   Event berbilang hari muncul pada SETIAP hari dalam julatnya, supaya kotak
   kalendar menunjukkan tempoh sebenar cuti sekolah. */
export function buildDayMap(y, m, config, showHidden = false) {
    const map = {};
    const total = daysInMonth(y, m);
    monthEvents(y, m, config, showHidden).forEach(e => {
        const last = e.until || e.date;
        for (let d = 1; d <= total; d++) {
            const k = dayKey(y, m, d);
            if (k >= e.date && k <= last) (map[k] || (map[k] = [])).push(e);
        }
    });
    return map;
}

/* ─── Strip "Akan datang" ──────────────────────────────
   Ambil event bermula hari ini, merentas sempadan bulan. Event kecil ditapis
   supaya strip tidak dipenuhi mid-month sale — pelanggan perlu nampak yang
   besar dahulu. */
export function upcomingEvents(config, count = 4, fromKey = klToday(), minTier = 'mid') {
    const [y, m] = fromKey.split('-').map(Number);
    const out = [];
    const seen = new Set();          // event berbilang hari muncul dlm 2 bulan — ambil sekali sahaja
    for (let i = 0; i < 6; i++) {
        const mm = ((m - 1 + i) % 12) + 1;
        const yy = y + Math.floor((m - 1 + i) / 12);
        monthEvents(yy, mm, config).forEach(e => {
            const last = e.until || e.date;
            if (last < fromKey) return;                                  // sudah berlalu
            if (TIER_RANK[e.tier] > TIER_RANK[minTier]) return;          // terlalu kecil
            if (seen.has(e.key)) return;
            seen.add(e.key);
            out.push({ ...e, daysAway: daysBetween(fromKey, e.date) });
        });
    }
    return out
        .sort((a, b) => a.date.localeCompare(b.date) || TIER_RANK[a.tier] - TIER_RANK[b.tier])
        .slice(0, count);
}

// Tarikh "mula sedia konten" = tarikh event tolak lead.
export const prepDate = (e) => shiftKey(e.date, -(e.lead ?? DEFAULT_LEAD[e.tier] ?? 7));

// ─── Kilang objek baharu (butang "＋ Event") ───────────
const uid = (p) => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
export const newEvent = (date) => ({
    id: uid('e'),
    date: date || klToday(),
    until: null,
    title: '',
    tier: 'mid',
    hint: '',
    lead: null,
    kind: 'custom',
});
